import { useCallback, useEffect, useState } from 'react';

import type { FormInstance } from '@arco-design/web-react';

import { getNodeConfig, getNodeList, getResource } from '../api';
import { DEFAULT_NODE_LIST_PAGINATION } from '../constants';
import type { BareMetalNodesPaths } from '../types';
import type { PhysicalNodeRecord } from '../utils/physicalNodeMeta';

interface UsePhysicalNodeListOptions {
  form: FormInstance;
  paths: BareMetalNodesPaths;
  billModelId?: string;
  isEdit?: boolean;
  initBmsNode?: unknown;
  /** preview / fixed 只读：不拉节点列表与 archTypes */
  skipListFetch?: boolean;
}

interface NodeListFilters {
  cpu: string;
  memory: string;
  arch: string;
  gpu: string;
  name: string;
}

interface NodeListPagination {
  page: number;
  limit: number;
  total: number;
}

/** 非计价模式：物理节点筛选、分页与列表拉取 */
export function usePhysicalNodeList({ form, paths, billModelId, isEdit, initBmsNode, skipListFetch }: UsePhysicalNodeListOptions) {
  const [nodes, setNodes] = useState<PhysicalNodeRecord[]>([]);
  const [queryObj, setQueryObj] = useState<Record<string, unknown>>({});
  const [archTypes, setArchTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<PhysicalNodeRecord>({});
  const [filters, setFilters] = useState<NodeListFilters>({
    cpu: '',
    memory: '',
    arch: '',
    gpu: '',
    name: '',
  });
  const [pagination, setPagination] = useState<NodeListPagination>({ ...DEFAULT_NODE_LIST_PAGINATION });

  const fetchNodes = useCallback(
    async (override: Record<string, unknown> = {}) => {
      if (billModelId || skipListFetch) return;

      setLoading(true);
      try {
        const query: Record<string, unknown> = {
          limit: pagination.limit,
          page: pagination.page,
          cpu: filters.cpu,
          memory: filters.memory,
          architecture: filters.arch,
          gpuModel: filters.gpu,
          status: 'up',
          name: filters.name,
          ...override,
        };

        const gpuNameValue = form.getFieldValue(paths.gpuName) as string | undefined;
        if (gpuNameValue) {
          query.gpu = `${gpuNameValue}|ALL`;
        }

        Object.keys(query).forEach((key) => {
          if (!query[key] && typeof query[key] !== 'boolean') {
            delete query[key];
          }
        });

        const { items, totalItems } = ((await getNodeList(query)) || {}) as {
          items?: PhysicalNodeRecord[];
          totalItems?: number;
        };

        if (Array.isArray(items)) {
          setNodes(items.map((item) => ({ ...item, key: item?.metadata?.name })));
        }

        setPagination({
          total: totalItems || 0,
          page: query.page as number,
          limit: query.limit as number,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [billModelId, filters, form, pagination.limit, pagination.page, paths.gpuName, skipListFetch],
  );

  const fetchArchTypes = useCallback(async () => {
    if (billModelId || skipListFetch) return;
    try {
      const { archTypes: list } = (await getNodeConfig()) as { archTypes?: string[] };
      setArchTypes(list || []);
    } catch (error) {
      console.log(error);
    }
  }, [billModelId, skipListFetch]);

  useEffect(() => {
    if (skipListFetch) return;

    getResource()
      .then((res) => setQueryObj((res as Record<string, unknown>) || {}))
      .catch(console.error);

    if (isEdit) {
      void fetchNodes({ name: initBmsNode });
    } else {
      void fetchArchTypes();
      void fetchNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时初始化列表
  }, []);

  const updateFilter = useCallback(
    (key: keyof NodeListFilters, value: string, queryOverride: Record<string, unknown> = {}) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      void fetchNodes({ [key === 'arch' ? 'architecture' : key === 'gpu' ? 'gpuModel' : key]: value, page: 1, ...queryOverride });
    },
    [fetchNodes],
  );

  const searchByName = useCallback(
    (value: string) => {
      setFilters((prev) => ({ ...prev, name: value }));
      void fetchNodes({ name: value, page: 1 });
    },
    [fetchNodes],
  );

  const changePage = useCallback(
    (page: number, limit: number) => {
      void fetchNodes({ page, limit });
    },
    [fetchNodes],
  );

  return {
    nodes,
    queryObj,
    archTypes,
    loading,
    selectedNode,
    setSelectedNode,
    pagination,
    filters,
    updateFilter,
    searchByName,
    changePage,
    refresh: () => void fetchNodes({ page: 1 }),
  };
}
